import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../../components/AppIcon';

import Select from '../../components/ui/Select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import TimePicker from './components/TimePicker';
import Loader from '../../components/ui/Loader';

import { MapPin, Phone, Calendar, Clock, Check, Zap, AlertCircle, X, Banknote, CreditCard, QrCode } from 'lucide-react';
import LocationInput from './components/LocationInput';

import PaymentModal from './components/PaymentModal';
import ReceiptModal from './components/ReceiptModal';
import QRPaymentModal from './components/QRPaymentModal';

import PriceDisplay from './components/PriceDisplay';
import Button from '../../components/ui/Button';

// ❌ REMOVE old pricing logic import
// import { calculatePrice, getAvailableServices } from '../../utils/pricingLogic';

// ✅ ADD new Supabase-backed pricing function
import { calculateFare, SERVICE_TYPES } from '../../utils/calculateFare';

// ✅ FIX: Change from @ alias to relative paths for Vite compatibility







import { useLanguage } from '../../contexts/LanguageContext';
import { getTranslation } from '../../utils/i18n/translations';
import { getDateFnsLocale } from '../../utils/i18n/config';
import { orderService } from '../../services/orderService';
import { useAuth } from '../../contexts/AuthContext';

import { useToast } from '../../contexts/ToastContext';

// ✅ FIX: Use the uploaded circular service icon for all services
import serviceIcon from '../../../public/assets/images/image-1765441193131.png';

const SmartBookingForm = () => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentLanguage } = useLanguage();
  const { user, isAuthenticated } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formStep, setFormStep] = useState(1);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [dateFnsLocale, setDateFnsLocale] = useState(null);
  const [bookingMode, setBookingMode] = useState('immediate');
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  const [orderError, setOrderError] = useState('');
  
  // Debug state for tracking submission flow
  const [lastBookingError, setLastBookingError] = useState(null);
  const [bookingStatus, setBookingStatus] = useState('idle'); // idle, submitting, success, error

  // Add guest success modal state
  const [showGuestSuccessModal, setShowGuestSuccessModal] = useState(false);
  const [showGuestModal, setShowGuestModal] = useState(false);

  // Stripe payment modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const [pendingPaymentMethod, setPendingPaymentMethod] = useState(null);
  const [showQRPaymentModal, setShowQRPaymentModal] = useState(false);

  // Initialize with current date and time
  const now = new Date();
  const initialDate = `${now?.getFullYear()}-${String(now?.getMonth() + 1)?.padStart(2, '0')}-${String(now?.getDate())?.padStart(2, '0')}`;
  const initialTime = `${String(now?.getHours())?.padStart(2, '0')}:${String(now?.getMinutes())?.padStart(2, '0')}`;

  // ✅ ZONE OPTIONS - matches user requirements exactly
  const ZONE_OPTIONS = [
    { label: 'Thong Sala', value: 'A' },
    { label: 'Baan Tai', value: 'B' },
    { label: 'Haad Rin', value: 'C' },
    { label: 'Srithanu', value: 'D' },
    { label: 'Haad Yao / Haad Salad', value: 'E' },
    { label: 'Chaloklum', value: 'F' },
    { label: 'Tong Nai Pan', value: 'G' },
    { label: 'Haad Yuan', value: 'H' }
  ];

  const [formData, setFormData] = useState({
    pickupLocation: '', // ✅ RESTORE pickupLocation (for display)
    destination: '', // ✅ RESTORE destination (for display)
    fromZone: '', // ✅ KEEP for backend pricing
    toZone: '', // ✅ KEEP for backend pricing
    pickupCoords: null, // ✅ ADD coordinates storage
    destinationCoords: null, // ✅ ADD coordinates storage
    date: initialDate,
    time: initialTime,
    passengers: '',
    selectedService: location?.state?.preselectedService || null,
    contactMethod: 'whatsapp',
    contactValue: '',
    isImmediate: true
  });

  const [errors, setErrors] = useState({});
  const [calculatedPrice, setCalculatedPrice] = useState(null);
  const [priceDetails, setPriceDetails] = useState(null);
  const [selectedDate, setSelectedDate] = useState(now);

  // Add a new state for tracking completed steps
  const [completedSteps, setCompletedSteps] = useState({
    locations: false,
    service: false,
    contact: false
  });

  // Add validation helper functions
  const validatePhoneNumber = (value) => {
    // Phone number should start with + and have at least 10 digits
    const phoneRegex = /^\+\d{10,15}$/;
    return phoneRegex?.test(value);
  };

  const validateTelegramContact = (value) => {
    // Telegram accepts both phone numbers and usernames
    const phoneRegex = /^\+\d{10,15}$/;
    const usernameRegex = /^@[a-zA-Z0-9_]{5,32}$/;
    return phoneRegex?.test(value) || usernameRegex?.test(value);
  };

  const validateContactValue = (method, value) => {
    if (!value || !value?.trim()) {
      return 'Please provide your contact information';
    }

    switch (method) {
      case 'whatsapp':
        if (!validatePhoneNumber(value)) {
          return 'Please enter a valid WhatsApp number (e.g., +66812345678)';
        }
        break;
      case 'telegram':
        if (!validateTelegramContact(value)) {
          return 'Please enter a valid phone number (+66812345678) or Telegram username (@username)';
        }
        break;
      case 'phone':
        if (!validatePhoneNumber(value)) {
          return 'Please enter a valid phone number (e.g., +66812345678)';
        }
        break;
      default:
        break;
    }
    return null;
  };

  // Load date-fns locale dynamically based on current language
  useEffect(() => {
    const loadLocale = async () => {
      try {
        const localeModule = await getDateFnsLocale(currentLanguage);
        setDateFnsLocale(localeModule?.default);
      } catch (error) {
        console.error('Error loading date-fns locale:', error);
        // Fallback to English locale
        const enLocale = await import('date-fns/locale/en-US');
        setDateFnsLocale(enLocale?.default);
      }
    };
    
    loadLocale();
  }, [currentLanguage]);

  // ✅ FIXED: Map single icon to all service types (all use same circular design)
  const SERVICE_TYPES_WITH_ICONS = SERVICE_TYPES?.map(service => {
    return {
      ...service,
      icon: serviceIcon // Use the same professional circular icon for all services
    };
  });

  // ✅ UPDATE availableServices to use SERVICE_TYPES_WITH_ICONS
  const availableServices = formData?.fromZone && formData?.toZone
    ? SERVICE_TYPES_WITH_ICONS?.filter(service => service?.key === 'sedan')
    : [];

  // ✅ Auto-select taxi (sedan) when zones are detected
  useEffect(() => {
    if (formData?.fromZone && formData?.toZone && !formData?.selectedService) {
      setFormData(prev => ({ ...prev, selectedService: 'sedan' }));
    }
  }, [formData?.fromZone, formData?.toZone]);

  // ✅ UPDATED useEffect - directly use fromZone and toZone
  useEffect(() => {
    const fetchPrice = async () => {
      if (!formData?.fromZone || !formData?.toZone || !formData?.selectedService) {
        setCalculatedPrice(null);
        setPriceDetails(null);
        return;
      }

      try {
        const price = await calculateFare({
          fromZone: formData?.fromZone,
          toZone: formData?.toZone,
          transportType: formData?.selectedService,
          passengerCount: parseInt(formData?.passengers) || 1,
          pickupDateTime: formData?.isImmediate ? new Date() : new Date(`${formData.date} ${formData.time}`),
          isFullMoon: false,
          weatherType: 'normal'
        });

        setCalculatedPrice(price);
        setPriceDetails({ 
          amount: price, 
          fromZone: formData?.fromZone, 
          toZone: formData?.toZone 
        });
      } catch (error) {
        console.error('Error calculating fare:', error);
        setCalculatedPrice(null);
      }
    };

    fetchPrice();
  }, [formData?.fromZone, formData?.toZone, formData?.selectedService, formData?.passengers, formData?.date, formData?.time, formData?.isImmediate]);

  /**
   * Extract zone code from location string
   * Assumes format like "Thong Sala (Zone A)" or similar
   * Falls back to simple mapping if needed
   */
  const extractZoneCode = (location) => {
    if (!location) return null;
    
    // Try to extract zone from parentheses
    const zoneMatch = location?.match(/\(Zone ([A-H])\)/i);
    if (zoneMatch) {
      return zoneMatch?.[1]?.toUpperCase();
    }

    // Fallback: Simple location to zone mapping
    const zoneMapping = {
      'Thong Sala': 'A',
      'Baan Tai': 'B',
      'Haad Rin': 'C',
      'Srithanu': 'D',
      'Haad Yao': 'E',
      'Salad': 'E',
      'Chaloklum': 'F',
      'Tong Nai Pan': 'G',
      'Haad Yuan': 'H'
    };

    for (const [key, zone] of Object.entries(zoneMapping)) {
      if (location?.toLowerCase()?.includes(key?.toLowerCase())) {
        return zone;
      }
    }

    return null;
  };

  // ✅ ADD expanded popular locations (more than 8)
  const popularLocations = [
    // Zone A - Thong Sala
    'Thong Sala Pier',
    'Thong Sala Town Center',
    'Pantip Market',
    'Thong Sala Walking Street',
    
    // Zone B - Baan Tai
    'Ban Tai Beach',
    'Baan Tai Village',
    'Secret Beach Baan Tai',
    
    // Zone C - Haad Rin
    'Haad Rin Beach',
    'Haad Rin Nok (Sunrise Beach)',
    'Haad Rin Nai (Sunset Beach)',
    'Leela Beach',
    'Long Beach',
    'Drop In Club',
    'Mountain Bar',
    
    // Zone D - Srithanu
    'Sri Thanu Beach',
    'Srithanu Village',
    'Haad Son Beach',
    'Malibu Beach',
    'Yoga Studio Srithanu',
    
    // Zone E - Haad Yao / Haad Salad
    'Haad Yao Beach',
    'Haad Salad Beach',
    'Koh Ma Beach',
    'Haad Yao East',
    
    // Zone F - Chaloklum
    'Ao Chaloklum',
    'Chaloklum Village',
    'Chaloklum Beach',
    
    // Zone G - Tong Nai Pan
    'Thong Nai Pan Noi Beach',
    'Thong Nai Pan Yai Beach',
    'Than Sadet Waterfall',
    
    // Zone H - Haad Yuan
    'Haad Yuan Beach',
    'Bottle Beach',
    'Had Khom Beach',
    
    // Central & Other Popular
    'Phaeng Waterfall',
    'Chinese Temple',
    'Wat Pho Temple',
    'Phangan Animal Care',
    'Koh Phangan Viewpoint',
    
    // Restaurants & Venues
    'Amsterdam Bar',
    'Guys Bar',
    'Pirates Bar',
    'Shiralea Backpackers Resort',
    'Phangan Jungle Experience',
    'Baan Thai Restaurant',
    'Fisherman Restaurant',
    'Nira Spa',
    
    // Hotels & Resorts
    'Santhiya Resort',
    'Anantara Rasananda',
    'Panviman Resort',
    'Buri Rasa Village',
    'Explorar Koh Phangan',
    'Havana Beach Resort',
    'Loyfa Natural Resort',
    'Sarikantang Resort',
    
    // Activities
    'Muay Thai Camp',
    'Diving School',
    'Jungle Trekking Point',
    'Mountain Bike Trails',
  ];

  // ✅ ADD zone detection function based on location name OR coordinates
  const detectZoneFromLocation = (locationName, coordinates = null) => {
    if (!locationName && !coordinates) return null;

    // Priority 1: Detect from location name
    const locationLower = locationName?.toLowerCase() || '';
    
    // Zone A - Thong Sala
    if (locationLower?.includes('thong sala') || locationLower?.includes('pantip')) {
      return 'A';
    }
    
    // Zone B - Baan Tai
    if (locationLower?.includes('ban tai') || locationLower?.includes('baan tai') || locationLower?.includes('secret beach')) {
      return 'B';
    }
    
    // Zone C - Haad Rin
    if (locationLower?.includes('haad rin') || locationLower?.includes('sunrise') || 
        locationLower?.includes('sunset') || locationLower?.includes('leela') || 
        locationLower?.includes('long beach') || locationLower?.includes('drop in') || 
        locationLower?.includes('mountain bar')) {
      return 'C';
    }
    
    // Zone D - Srithanu
    if (locationLower?.includes('sri thanu') || locationLower?.includes('srithanu') || 
        locationLower?.includes('haad son') || locationLower?.includes('malibu')) {
      return 'D';
    }
    
    // Zone E - Haad Yao / Haad Salad
    if (locationLower?.includes('haad yao') || locationLower?.includes('haad salad') || 
        locationLower?.includes('koh ma')) {
      return 'E';
    }
    
    // Zone F - Chaloklum
    if (locationLower?.includes('chaloklum')) {
      return 'F';
    }
    
    // Zone G - Tong Nai Pan
    if (locationLower?.includes('tong nai pan') || locationLower?.includes('thong nai pan') || 
        locationLower?.includes('than sadet')) {
      return 'G';
    }
    
    // Zone H - Haad Yuan
    if (locationLower?.includes('haad yuan') || locationLower?.includes('bottle beach') || 
        locationLower?.includes('had khom')) {
      return 'H';
    }

    // Priority 2: If coordinates available, use geographic detection
    // (This would require more complex logic - for now return null)
    
    // Default fallback
    return null;
  };

  // ✅ ADD handler for location changes with automatic zone detection
  const handleLocationChange = (field, value, coords = null) => {
    const detectedZone = detectZoneFromLocation(value, coords);
    
    setFormData(prev => ({
      ...prev,
      [field]: value,
      [`${field}Coords`]: coords,
      [field === 'pickupLocation' ? 'fromZone' : 'toZone']: detectedZone || prev?.[field === 'pickupLocation' ? 'fromZone' : 'toZone'],
      selectedService: 'sedan' // Always keep taxi as default
    }));
  };

  // ✅ UPDATE completed steps to check both location AND zone
  useEffect(() => {
    setCompletedSteps({
      locations: !!(formData?.pickupLocation && formData?.destination && formData?.fromZone && formData?.toZone),
      service: true, // Always true — taxi is the only service
      contact: !!(formData?.contactValue?.trim() && !validateContactValue(formData?.contactMethod, formData?.contactValue))
    });
  }, [formData?.pickupLocation, formData?.destination, formData?.fromZone, formData?.toZone, formData?.selectedService, formData?.contactValue, formData?.contactMethod]);

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData?.pickupLocation) {
        newErrors.pickupLocation = 'Please select pickup location';
      }
      if (!formData?.destination) {
        newErrors.destination = 'Please select destination';
      }
      // ✅ ADD zone validation
      if (!formData?.fromZone) {
        newErrors.fromZone = 'Could not detect pickup zone. Please select a recognized location.';
      }
      if (!formData?.toZone) {
        newErrors.toZone = 'Could not detect destination zone. Please select a recognized location.';
      }
      if (bookingMode === 'scheduled') {
        if (!formData?.date) {
          newErrors.date = 'This field is required';
        }
        if (!formData?.time) {
          newErrors.time = 'This field is required';
        }
      }
    }

    if (step === 2) {
      if (!formData?.contactMethod) {
        newErrors.contactMethod = 'Please select a contact method';
      }
      
      // Enhanced contact validation with format checking
      const contactError = validateContactValue(formData?.contactMethod, formData?.contactValue);
      if (contactError) {
        newErrors.contactValue = contactError;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const validateBeforeSubmit = () => {
    const validationErrors = {};

    if (!formData?.pickupLocation) {
      validationErrors.pickupLocation = 'Pickup location is required';
    }
    if (!formData?.destination) {
      validationErrors.destination = 'Destination is required';
    }

    // Step 3: Time validation (only for scheduled bookings)
    if (bookingMode === 'scheduled') {
      if (!formData?.date) {
        validationErrors.date = 'Date is required';
      }
      if (!formData?.time) {
        validationErrors.time = 'Time is required';
      }
    }

    // Step 4: Contact validation
    const contactError = validateContactValue(formData?.contactMethod, formData?.contactValue);
    if (contactError) {
      validationErrors.contactValue = contactError;
    }

    setErrors(validationErrors);
    return Object.keys(validationErrors)?.length === 0;
  };

  const handleNext = () => {
    if (validateStep(formStep)) {
      if (formStep < 3) {
        setFormStep(formStep + 1);
      } else {
        setShowConfirmation(true);
      }
    }
  };

  const handleBack = () => {
    if (formStep > 1) {
      setFormStep(formStep - 1);
      setErrors({});
    }
  };

  const handleClose = () => {
    navigate(-1);
  };

  const handleConfirmBooking = () => {
    setShowConfirmation(false);
    setTimeout(() => {
      navigate('/home-landing');
    }, 1000);
  };

  const handleServiceSelect = (service) => {
    setFormData(prev => ({
      ...prev,
      selectedService: service
    }));
  };

  const getSelectedService = () => {
    return availableServices?.find(s => s?.key === formData?.selectedService);
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    if (date) {
      // Format date as YYYY-MM-DD for form data
      const year = date?.getFullYear();
      const month = String(date?.getMonth() + 1)?.padStart(2, '0');
      const day = String(date?.getDate())?.padStart(2, '0');
      setFormData({ ...formData, date: `${year}-${month}-${day}` });
    } else {
      setFormData({ ...formData, date: '' });
    }
  };

  const handleImmediateBooking = () => {
    setBookingMode('immediate');
    // Auto-populate current date and time
    const now = new Date();
    const currentDate = `${now?.getFullYear()}-${String(now?.getMonth() + 1)?.padStart(2, '0')}-${String(now?.getDate())?.padStart(2, '0')}`;
    const currentTime = `${String(now?.getHours())?.padStart(2, '0')}:${String(now?.getMinutes())?.padStart(2, '0')}`;
    
    setFormData(prev => ({
      ...prev,
      date: currentDate,
      time: currentTime,
      isImmediate: true
    }));
    setSelectedDate(now);
  };

  const handleScheduledBooking = () => {
    setBookingMode('scheduled');
    
    // Set current date and time for scheduled bookings
    const now = new Date();
    const currentDate = `${now?.getFullYear()}-${String(now?.getMonth() + 1)?.padStart(2, '0')}-${String(now?.getDate())?.padStart(2, '0')}`;
    const currentTime = `${String(now?.getHours())?.padStart(2, '0')}:${String(now?.getMinutes())?.padStart(2, '0')}`;
    
    setFormData(prev => ({
      ...prev,
      date: currentDate,
      time: currentTime,
      isImmediate: false
    }));
    setSelectedDate(now);
  };

  const handleTimeChange = (time) => {
    setFormData(prev => ({ ...prev, time }));
  };

  const bookingData = {
    fromZone: formData?.fromZone,
    toZone: formData?.toZone,
    date: formData?.date,
    time: formData?.time,
    passengers: formData?.passengers,
    service: getSelectedService()?.name || '',
    contactMethod: formData?.contactMethod,
    price: calculatedPrice,
    priceDetails: priceDetails
  };

  // ✅ UPDATED: Use real WhatsApp number from environment variable
  const buildWhatsAppLink = (paymentMethod) => {
    const whatsappPhone = import.meta.env?.VITE_WHATSAPP_BOOKING_PHONE || '66841823265'; // ✅ Updated to use env var
    
    // Get zone labels from ZONE_OPTIONS
    const getZoneLabel = (code) => {
      const zone = ZONE_OPTIONS?.find(z => z?.value === code);
      return zone ? zone?.label : code;
    };

    const pickupArea = formData?.pickupLocation || getZoneLabel(formData?.fromZone);
    const destinationArea = formData?.destination || getZoneLabel(formData?.toZone);
    const serviceName = getSelectedService()?.name || 'Not selected';
    const timeInfo = formData?.isImmediate
      ? 'Now (ASAP)'
      : `${formData?.date} at ${formData?.time}`;
    const priceValue = calculatedPrice || 'Calculating...';
    const paymentLabels = { cash: 'Cash', card: 'Card', qr: 'QR Code' };
    const paymentInfo = paymentMethod ? `\nPayment: ${paymentLabels?.[paymentMethod] || paymentMethod}` : '';

    const message = `Phangan Taxi booking request:
From: ${pickupArea}
To: ${destinationArea}
Service: ${serviceName}
Time: ${timeInfo}
Price: ${priceValue} THB${paymentInfo}`;

    const encodedMessage = encodeURIComponent(message);
    return `https://wa.me/${whatsappPhone}?text=${encodedMessage}`; // ✅ Correct format (no + in URL)
  };

  // Build booking details for Stripe payment
  const buildPaymentDetails = () => {
    const getZoneLabel = (code) => {
      const zone = ZONE_OPTIONS?.find(z => z?.value === code);
      return zone ? zone?.label : code;
    };
    return {
      amount: calculatedPrice,
      from: formData?.pickupLocation || getZoneLabel(formData?.fromZone),
      to: formData?.destination || getZoneLabel(formData?.toZone),
      fromZone: formData?.fromZone,
      toZone: formData?.toZone,
      serviceType: getSelectedService()?.name || 'Taxi',
      orderId: null,
      userId: user?.id || null,
      customerEmail: user?.email || null,
      customerName: user?.email || null,
      stripeCustomerId: null,
    };
  };

  // Handle card payment button click
  const handleCardPayment = () => {
    setShowGuestModal(false);
    setPendingPaymentMethod('card');
    setShowPaymentModal(true);
  };

  // Handle QR payment button click
  const handleQRPayment = () => {
    setShowGuestModal(false);
    setPendingPaymentMethod('qr');
    setShowQRPaymentModal(true);
  };

  // Handle QR payment success
  const handleQRPaymentSuccess = (paymentResult) => {
    setShowQRPaymentModal(false);
    const getZoneLabel = (code) => {
      const zone = ZONE_OPTIONS?.find(z => z?.value === code);
      return zone ? zone?.label : code;
    };
    const receipt = {
      transactionId: paymentResult?.paymentIntentId,
      amount: calculatedPrice,
      from: formData?.pickupLocation || getZoneLabel(formData?.fromZone),
      to: formData?.destination || getZoneLabel(formData?.toZone),
      serviceType: getSelectedService()?.name || 'Taxi',
      paymentMethod: 'promptpay',
      paymentStatus: 'succeeded',
      stripeReceiptUrl: null,
      scheduledTime: formData?.isImmediate ? 'Now (ASAP)' : `${formData?.date} at ${formData?.time}`,
      createdAt: new Date()?.toISOString(),
    };
    setReceiptData(receipt);
    setShowReceiptModal(true);
    showToast('Payment successful! Your booking is confirmed.', 'success');
  };

  // Handle payment success from Stripe
  const handlePaymentSuccess = (paymentResult) => {
    setShowPaymentModal(false);
    const getZoneLabel = (code) => {
      const zone = ZONE_OPTIONS?.find(z => z?.value === code);
      return zone ? zone?.label : code;
    };
    const receipt = {
      transactionId: paymentResult?.paymentIntentId,
      amount: calculatedPrice,
      from: formData?.pickupLocation || getZoneLabel(formData?.fromZone),
      to: formData?.destination || getZoneLabel(formData?.toZone),
      serviceType: getSelectedService()?.name || 'Taxi',
      paymentMethod: 'card',
      paymentStatus: 'succeeded',
      stripeReceiptUrl: paymentResult?.receiptUrl || null,
      scheduledTime: formData?.isImmediate ? 'Now (ASAP)' : `${formData?.date} at ${formData?.time}`,
      createdAt: new Date()?.toISOString(),
    };
    setReceiptData(receipt);
    setShowReceiptModal(true);
    showToast('Payment successful! Your booking is confirmed.', 'success');
  };

  // NEW: Save booking context to localStorage
  const saveBookingContext = () => {
    const context = {
      fromZone: formData?.fromZone,
      toZone: formData?.toZone,
      date: formData?.date,
      time: formData?.time,
      passengers: formData?.passengers,
      selectedService: formData?.selectedService,
      contactMethod: formData?.contactMethod,
      contactValue: formData?.contactValue,
      isImmediate: formData?.isImmediate,
      calculatedPrice: calculatedPrice,
      timestamp: new Date()?.getTime()
    };
    localStorage.setItem('pendingBooking', JSON.stringify(context));
  };

  // NEW: Restore booking context from localStorage (call on mount)
  useEffect(() => {
    const pendingBooking = localStorage.getItem('pendingBooking');
    if (pendingBooking) {
      try {
        const context = JSON.parse(pendingBooking);
        // Check if context is less than 1 hour old
        const isRecent = (new Date()?.getTime() - context?.timestamp) < 3600000;
        if (isRecent && context?.fromZone && context?.toZone) {
          setFormData({
            pickupLocation: context?.pickupLocation || '',
            destination: context?.destination || '',
            fromZone: context?.fromZone,
            toZone: context?.toZone,
            pickupCoords: context?.pickupCoords,
            destinationCoords: context?.destinationCoords,
            date: context?.date,
            time: context?.time,
            passengers: context?.passengers,
            selectedService: context?.selectedService,
            contactMethod: context?.contactMethod,
            contactValue: context?.contactValue,
            isImmediate: context?.isImmediate
          });
          if (context?.calculatedPrice) {
            setCalculatedPrice(context?.calculatedPrice);
          }
          // Clear the stored context
          localStorage.removeItem('pendingBooking');
          showToast('Your booking details have been restored', 'success');
        }
      } catch (error) {
        console.error('Error restoring booking context:', error);
      }
    }
  }, []);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    // 🚨 CRITICAL: Check authentication FIRST - prevent Supabase calls for guests
    if (!isAuthenticated) {
      // Show modal instead of toast
      setShowGuestModal(true);
      return; // STOP HERE - no Supabase calls
    }

    // Update booking status for debugging
    setBookingStatus('submitting');
    setLastBookingError(null);
    
    // ✅ Use calculatedPrice from state (already fetched from Supabase)
    if (!calculatedPrice || calculatedPrice === null || calculatedPrice === undefined) {
      const errorMsg = 'Price calculation error. Please select service again.';
      showToast(errorMsg, 'error');
      setLastBookingError(errorMsg);
      setBookingStatus('error');
      setIsSubmitting(false);
      return;
    }
    
    // Validation
    if (!canProceed()) {
      const errorMsg = 'Please fill in all required fields';
      showToast(errorMsg, 'error');
      setLastBookingError(errorMsg);
      setBookingStatus('error');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const orderData = {
        pickupLocation: formData?.pickupLocation,
        destination: formData?.destination,
        customerName: user?.email || 'Guest',
        customerPhone: formData?.contactValue?.startsWith('+') ? formData?.contactValue : '',
        fareAmount: calculatedPrice, // ✅ Use price from state (calculated via Supabase)
        serviceType: formData?.selectedService,
        pickupTime: formData?.isImmediate ? 'NOW()' : `${formData?.date} ${formData?.time}`,
        scheduledDate: formData?.isImmediate ? null : formData?.date,
        scheduledTime: formData?.isImmediate ? null : formData?.time,
        passengers: formData?.passengers || 1,
        contactMethod: formData?.contactMethod,
        isImmediate: formData?.isImmediate ?? true
      };

      const createdOrder = await orderService?.createOrder(orderData);
      
      setBookingStatus('success');
      setShowConfirmation(true);
      showToast('Booking created successfully!', 'success');
      
    } catch (error) {
      console.error('Booking error:', error);
      const errorMsg = error?.message || 'Failed to create booking';
      showToast(errorMsg, 'error');
      setLastBookingError(errorMsg);
      setBookingStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ✅ UPDATE handleCloseConfirmation to reset both locations and zones
  const handleCloseConfirmation = () => {
    setShowConfirmation(false);
    
    setFormData({
      pickupLocation: '',
      destination: '',
      fromZone: '',
      toZone: '',
      pickupCoords: null,
      destinationCoords: null,
      date: '',
      time: '',
      passengers: '',
      selectedService: null,
      contactMethod: 'whatsapp',
      contactValue: '',
      isImmediate: true
    });
    
    // Reset other states
    setSelectedDate(null);
    setCalculatedPrice(null);
    setPriceDetails(null);
    setErrors({});
    setFormStep(1);
    
    // Re-initialize immediate booking mode with current date/time
    const now = new Date();
    const currentDate = `${now?.getFullYear()}-${String(now?.getMonth() + 1)?.padStart(2, '0')}-${String(now?.getDate())?.padStart(2, '0')}`;
    const currentTime = `${String(now?.getHours())?.padStart(2, '0')}:${String(now?.getMinutes())?.padStart(2, '0')}`;
    
    setFormData(prev => ({
      ...prev,
      date: currentDate,
      time: currentTime,
      isImmediate: true
    }));
    setSelectedDate(now);
    setBookingMode('immediate');
  };

  const canProceed = () => {
    const hasRequiredFields = formData?.pickupLocation &&
      formData?.destination &&
      formData?.contactValue?.trim();

    if (!hasRequiredFields) {
      return false;
    }

    if (!calculatedPrice || calculatedPrice === null || calculatedPrice === undefined) {
      return false;
    }

    if (bookingMode === 'scheduled') {
      if (!formData?.date || !formData?.time) {
        return false;
      }
    }

    const contactError = validateContactValue(formData?.contactMethod, formData?.contactValue);
    if (contactError) {
      return false;
    }

    return true;
  };

  const reset = () => {
    // Reset form to initial state
    setFormData({
      pickupLocation: '',
      destination: '',
      fromZone: '',
      toZone: '',
      pickupCoords: null,
      destinationCoords: null,
      date: '',
      time: '',
      passengers: '',
      selectedService: null,
      contactMethod: 'whatsapp',
      contactValue: '',
      isImmediate: true
    });
    
    // Reset other states
    setSelectedDate(null);
    setCalculatedPrice(null);
    setPriceDetails(null);
    setErrors({});
    setFormStep(1);
    
    // Re-initialize immediate booking mode with current date/time
    const now = new Date();
    const currentDate = `${now?.getFullYear()}-${String(now?.getMonth() + 1)?.padStart(2, '0')}-${String(now?.getDate())?.padStart(2, '0')}`;
    const currentTime = `${String(now?.getHours())?.padStart(2, '0')}:${String(now?.getMinutes())?.padStart(2, '0')}`;
    
    setFormData(prev => ({
      ...prev,
      date: currentDate,
      time: currentTime,
      isImmediate: true
    }));
    setSelectedDate(now);
    setBookingMode('immediate');
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      {/* Stripe Payment Modal */}
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        bookingDetails={buildPaymentDetails()}
        onPaymentSuccess={handlePaymentSuccess}
      />

      {/* Receipt Modal */}
      <ReceiptModal
        isOpen={showReceiptModal}
        onClose={() => {
          setShowReceiptModal(false);
          setReceiptData(null);
        }}
        receiptData={receiptData}
      />

      {/* QR PromptPay Payment Modal */}
      <QRPaymentModal
        isOpen={showQRPaymentModal}
        onClose={() => setShowQRPaymentModal(false)}
        bookingDetails={buildPaymentDetails()}
        onPaymentSuccess={handleQRPaymentSuccess}
      />

      {/* NEW: Guest Booking Modal */}
      {showGuestModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative animate-fadeIn">
            {/* Close button */}
            <button
              onClick={() => setShowGuestModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Icon */}
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-10 h-10 text-blue-600" />
            </div>

            {/* Title */}
            <h2 className="text-2xl font-bold text-gray-900 mb-3 text-center">
              Your Booking Almost Ready
            </h2>

            {/* Message */}
            <p className="text-base text-gray-600 mb-4 text-center leading-relaxed">
              Confirm and choose payment method
            </p>

            {/* Booking Summary Preview */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Route:</span>
                <span className="font-semibold text-gray-900">
                  {ZONE_OPTIONS?.find(z => z?.value === formData?.fromZone)?.label} → {ZONE_OPTIONS?.find(z => z?.value === formData?.toZone)?.label}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Service:</span>
                <span className="font-semibold text-gray-900">{getSelectedService()?.name}</span>
              </div>
              {calculatedPrice && (
                <div className="flex justify-between text-sm pt-2 border-t border-gray-200">
                  <span className="text-gray-600">Price:</span>
                  <span className="font-bold text-blue-600">{calculatedPrice} THB</span>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2 mb-3">
                <button
                  onClick={() => {
                    const whatsappLink = buildWhatsAppLink('cash');
                    window.open(whatsappLink, '_blank');
                    setShowGuestModal(false);
                  }}
                  className="flex flex-col items-center justify-center gap-1.5 bg-gray-50 hover:bg-green-50 border-2 border-gray-200 hover:border-green-500 text-gray-700 hover:text-green-700 font-semibold py-3 px-2 rounded-xl transition-all duration-200"
                >
                  <Banknote className="w-6 h-6" />
                  <span className="text-xs">Cash</span>
                </button>
                <button
                  onClick={handleCardPayment}
                  className="flex flex-col items-center justify-center gap-1.5 bg-gray-50 hover:bg-blue-50 border-2 border-gray-200 hover:border-blue-500 text-gray-700 hover:text-blue-700 font-semibold py-3 px-2 rounded-xl transition-all duration-200"
                >
                  <CreditCard className="w-6 h-6" />
                  <span className="text-xs">Card</span>
                </button>
                <button
                  onClick={handleQRPayment}
                  className="flex flex-col items-center justify-center gap-1.5 bg-gray-50 hover:bg-purple-50 border-2 border-gray-200 hover:border-purple-500 text-gray-700 hover:text-purple-700 font-semibold py-3 px-2 rounded-xl transition-all duration-200"
                >
                  <QrCode className="w-6 h-6" />
                  <span className="text-xs">QR Code</span>
                </button>
              </div>

              <button
                onClick={() => setShowGuestModal(false)}
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 rounded-xl transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Success Confirmation Modal */}
      {showConfirmation && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-3 xxs:p-4 sm:p-6">
          <div className="bg-white rounded-xl xxs:rounded-2xl shadow-2xl max-w-md w-full p-6 xxs:p-8 text-center animate-fadeIn">
            <div className="w-16 h-16 xxs:w-20 xxs:h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 xxs:mb-6">
              <Check className="w-10 h-10 xxs:w-12 xxs:h-12 text-green-600" />
            </div>
            <h2 className="text-xl xxs:text-2xl font-bold text-gray-900 mb-2">
              Booking Confirmed!
            </h2>
            <p className="text-sm xxs:text-base text-gray-600 mb-4 xxs:mb-6 break-words hyphens-auto leading-relaxed px-2">
              {formData?.isImmediate 
                ? 'Your ride request has been submitted. A driver will contact you shortly!' :'Your booking has been confirmed. You will receive details via your chosen contact method.'
              }
            </p>

            {/* Booking Summary */}
            <div className="space-y-2 xxs:space-y-3 text-left bg-gray-50 rounded-lg p-3 xxs:p-4 max-w-full overflow-hidden">
              <div className="flex items-start justify-between gap-2 xxs:gap-3">
                <span className="text-xs xxs:text-sm text-gray-600 flex-shrink-0 min-w-[70px] xxs:min-w-[80px]">From:</span>
                <span className="text-xs xxs:text-sm font-semibold text-gray-900 text-right break-words overflow-wrap-anywhere flex-1">{formData?.pickupLocation}</span>
              </div>
              <div className="flex items-start justify-between gap-2 xxs:gap-3">
                <span className="text-xs xxs:text-sm text-gray-600 flex-shrink-0 min-w-[70px] xxs:min-w-[80px]">To:</span>
                <span className="text-xs xxs:text-sm font-semibold text-gray-900 text-right break-words overflow-wrap-anywhere flex-1">{formData?.destination}</span>
              </div>
              <div className="flex items-start justify-between gap-2 xxs:gap-3">
                <span className="text-xs xxs:text-sm text-gray-600 flex-shrink-0 min-w-[70px] xxs:min-w-[80px]">When:</span>
                <span className="text-xs xxs:text-sm font-semibold text-gray-900 text-right break-words overflow-wrap-anywhere flex-1">
                  {formData?.isImmediate ? 'Now (ASAP)' : `${formData?.date} at ${formData?.time}`}
                </span>
              </div>
              <div className="flex items-start justify-between gap-2 xxs:gap-3">
                <span className="text-xs xxs:text-sm text-gray-600 flex-shrink-0 min-w-[70px] xxs:min-w-[80px]">Service:</span>
                <span className="text-xs xxs:text-sm font-semibold text-gray-900 text-right break-words overflow-wrap-anywhere flex-1">{getSelectedService()?.name}</span>
              </div>
              {calculatedPrice && (
                <div className="flex items-center justify-between pt-2 xxs:pt-3 border-t border-gray-200">
                  <span className="text-xs xxs:text-sm font-semibold text-gray-900">Price:</span>
                  <span className="text-base xxs:text-lg font-bold text-blue-600">{calculatedPrice} THB</span>
                </div>
              )}
            </div>

            {/* Price Disclaimer - Updated with plain English text */}
            <div className="mt-4 xxs:mt-5 bg-yellow-50 border border-yellow-200 rounded-lg p-3 xxs:p-4">
              <p className="text-xs xxs:text-sm text-yellow-800 text-center break-words hyphens-auto leading-relaxed">
                We will confirm the exact price with you in chat if conditions change.
              </p>
            </div>

            {/* Action Buttons - Updated button text and navigation logic */}
            <div className="mt-4 xxs:mt-6 space-y-2 xxs:space-y-3">
              {isAuthenticated ? (
                <button
                  onClick={() => {
                    setShowConfirmation(false);
                    navigate('/customer-order-history');
                  }}
                  className="w-full py-2.5 xxs:py-3 px-4 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white text-sm xxs:text-base font-semibold rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  View my trips
                </button>
              ) : (
                <button
                  onClick={() => setShowConfirmation(false)}
                  className="w-full py-2.5 xxs:py-3 px-4 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white text-sm xxs:text-base font-semibold rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  Close
                </button>
              )}
              
              <button
                onClick={handleCloseConfirmation}
                className="w-full py-2.5 xxs:py-3 px-4 bg-white hover:bg-gray-50 text-gray-700 text-sm xxs:text-base font-semibold rounded-xl transition-all duration-200 border border-gray-300"
              >
                Book Another Ride
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Guest Success Modal */}
      {showGuestSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
                <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Booking Confirmed!</h3>
              <p className="text-gray-600 mb-6">
                Your ride has been booked successfully. We'll contact you at the provided phone number.
              </p>
              <div className="space-y-3">
                <button
                  onClick={() => {
                    setShowGuestSuccessModal(false);
                    navigate('/customer-login');
                  }}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg transition-colors"
                >
                  Sign In / Register to View Order History
                </button>
                <button
                  onClick={() => setShowGuestSuccessModal(false)}
                  className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 rounded-lg transition-colors"
                >
                  Continue as Guest
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Main Form Container */}
      <div className="max-w-[1400px] 2xl:max-w-[1600px] mx-auto px-4 xs:px-6 sm:px-8 md:px-10 lg:px-12 xl:px-16 py-6 sm:py-8 md:py-10 lg:py-12">
        {/* Step Progress Indicator */}
        <div className="mb-6 sm:mb-8">
          <div className="bg-white rounded-xl shadow-md p-4 sm:p-6">
            <div className="flex items-center justify-between">
              {/* Step 1: Locations */}
              <div className="flex items-center flex-1">
                <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center font-bold ${
                  completedSteps?.locations ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-600'
                }`}>
                  {completedSteps?.locations ? <Check size={20} /> : '1'}
                </div>
                <span className="ml-2 text-xs sm:text-sm font-medium text-gray-700 hidden sm:inline">Locations</span>
              </div>
              
              <div className="h-0.5 flex-1 bg-gray-200 mx-2">
                <div className={`h-full ${completedSteps?.locations ? 'bg-green-500' : 'bg-gray-200'} transition-all duration-300`} 
                     style={{ width: completedSteps?.locations ? '100%' : '0%' }} />
              </div>

              {/* Step 2: Service */}
              <div className="flex items-center flex-1">
                <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center font-bold ${
                  completedSteps?.service ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-600'
                }`}>
                  {completedSteps?.service ? <Check size={20} /> : '2'}
                </div>
                <span className="ml-2 text-xs sm:text-sm font-medium text-gray-700 hidden sm:inline">Service</span>
              </div>
              
              <div className="h-0.5 flex-1 bg-gray-200 mx-2">
                <div className={`h-full ${completedSteps?.service ? 'bg-green-500' : 'bg-gray-200'} transition-all duration-300`} 
                     style={{ width: completedSteps?.service ? '100%' : '0%' }} />
              </div>

              {/* Step 3: Contact */}
              <div className="flex items-center flex-1">
                <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center font-bold ${
                  completedSteps?.contact ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-600'
                }`}>
                  {completedSteps?.contact ? <Check size={20} /> : '3'}
                </div>
                <span className="ml-2 text-xs sm:text-sm font-medium text-gray-700 hidden sm:inline">Contact</span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 xl:grid-cols-[2fr_1fr] gap-6 sm:gap-8 lg:gap-10 xl:gap-12">
          {/* Main Form */}
          <div className="lg:col-span-2 xl:col-span-1 space-y-6 sm:space-y-8">
            {/* Trip Details Card */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden" data-field="locations">
              <div className="p-6 sm:p-8 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-purple-50">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center">
                  <MapPin className="w-6 h-6 sm:w-7 sm:h-7 mr-3 text-blue-600 flex-shrink-0" />
                  <span className="truncate">{getTranslation('booking.form.tripDetailsTitle', currentLanguage)}</span>
                </h2>
              </div>

              <div className="p-6 sm:p-8 space-y-6">
                {/* Booking Mode Selection - Improved for widescreen */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <button
                    onClick={handleImmediateBooking}
                    className={`p-5 rounded-xl border-2 transition-all duration-200 hover:scale-[1.02] ${
                      bookingMode === 'immediate' ?'border-green-500 bg-green-50 shadow-md ring-2 ring-green-200' :'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <Zap className={`w-6 h-6 flex-shrink-0 ${bookingMode === 'immediate' ? 'text-green-600' : 'text-gray-400'}`} />
                      <span className={`text-base sm:text-lg font-semibold ${bookingMode === 'immediate' ? 'text-green-900' : 'text-gray-700'}`}>
                        {getTranslation('booking.form.immediateBooking', currentLanguage) || 'Order Now'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 text-left">
                      {getTranslation('booking.form.immediateDescription', currentLanguage) || 'Nearest available vehicle'}
                    </p>
                  </button>

                  <button
                    onClick={handleScheduledBooking}
                    className={`p-5 rounded-xl border-2 transition-all duration-200 hover:scale-[1.02] ${
                      bookingMode === 'scheduled' ?'border-blue-500 bg-blue-50 shadow-md ring-2 ring-blue-200' :'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <Calendar className={`w-6 h-6 flex-shrink-0 ${bookingMode === 'scheduled' ? 'text-blue-600' : 'text-gray-400'}`} />
                      <span className={`text-base sm:text-lg font-semibold ${bookingMode === 'scheduled' ? 'text-blue-900' : 'text-gray-700'}`}>
                        {getTranslation('booking.form.scheduledBooking', currentLanguage) || 'Schedule for Later'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 text-left">
                      {getTranslation('booking.form.scheduledDescription', currentLanguage) || 'Choose date and time'}
                    </p>
                  </button>
                </div>

                {/* Immediate Booking Notice - Responsive */}
                {bookingMode === 'immediate' && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 xxs:p-4">
                    <div className="flex items-start gap-2 xxs:gap-3">
                      <Zap className="w-4 h-4 xxs:w-5 xxs:h-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs xxs:text-sm font-semibold text-green-900 mb-1">
                          {getTranslation('booking.form.immediateNoticeTitle', currentLanguage) || 'Instant Pickup Request'}
                        </p>
                        <p className="text-xs text-green-700">
                          {getTranslation('booking.form.immediateNoticeMessage', currentLanguage) || 'The nearest available driver will be notified immediately. Typical pickup time: 5-15 minutes.'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Date and Time - shown directly below booking mode selector when scheduled */}
                {bookingMode === 'scheduled' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Date Picker - Perfectly matched with TimePicker */}
                    <div className="space-y-2">
                      <label className="block text-sm font-semibold text-gray-900">
                        {getTranslation('booking.form.dateLabel', currentLanguage) || 'Date'}
                        <span className="text-red-500 ml-1">*</span>
                      </label>
                      <div className="relative h-[46px]">
                        <DatePicker
                          selected={selectedDate}
                          onChange={handleDateChange}
                          locale={dateFnsLocale}
                          dateFormat="dd.MM.yyyy"
                          minDate={new Date()}
                          placeholderText={getTranslation('booking.form.datePlaceholder', currentLanguage) || 'Select date'}
                          className="w-full h-[46px] px-4 py-3 text-base rounded-lg bg-white border-2 border-gray-300 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 cursor-pointer hover:border-blue-400"
                          wrapperClassName="w-full h-full"
                          calendarClassName="!shadow-2xl !border-2 !border-blue-200"
                          popperClassName="!z-[9999]"
                          popperPlacement="top-start"
                          popperModifiers={[
                            {
                              name: 'offset',
                              options: {
                                offset: [0, 8],
                              },
                            },
                            {
                              name: 'preventOverflow',
                              options: {
                                rootBoundary: 'viewport',
                                padding: 8,
                              },
                            },
                            {
                              name: 'flip',
                              options: {
                                fallbackPlacements: ['bottom-start', 'top-end', 'bottom-end'],
                              },
                            },
                          ]}
                          showPopperArrow={true}
                          autoComplete="off"
                        />
                        <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                      </div>
                      {errors?.date && (
                        <p className="text-sm text-red-500 flex items-center gap-1">
                          <AlertCircle className="w-4 h-4" />
                          Please select a date for this trip
                        </p>
                      )}
                    </div>

                    {/* Time Picker - Now perfectly aligned with Date Picker */}
                    <TimePicker
                      label={getTranslation('booking.form.timeLabel', currentLanguage)}
                      value={formData?.time}
                      onChange={handleTimeChange}
                      error={errors?.time}
                      required
                      selectedDate={selectedDate}
                    />
                    
                    {/* Validation message for missing date/time */}
                    {(!formData?.date || !formData?.time) && (
                      <div className="col-span-2 bg-yellow-50 border-l-4 border-yellow-400 rounded-lg p-4">
                        <div className="flex items-center gap-2">
                          <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0" />
                          <p className="text-sm text-yellow-800 font-medium">
                            Please select date and time for this trip
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* ✅ REPLACE zone Select with LocationInput for pickup */}
                <div className="space-y-2">
                  <LocationInput
                    label="Pickup Location"
                    value={formData?.pickupLocation}
                    onChange={(value, coords) => handleLocationChange('pickupLocation', value, coords)}
                    placeholder="Enter pickup location or select on map"
                    popularLocations={popularLocations}
                    error={errors?.pickupLocation || errors?.fromZone}
                  />
                  {/* Show detected zone */}
                  {formData?.pickupLocation && formData?.fromZone && (
                    <div className="flex items-center gap-2 text-xs text-gray-600 bg-blue-50 rounded-lg px-3 py-2">
                      <MapPin className="w-4 h-4 text-blue-600" />
                      <span>Zone <strong className="text-blue-700">{formData?.fromZone}</strong> detected</span>
                    </div>
                  )}
                  {/* Show zone detection error */}
                  {formData?.pickupLocation && !formData?.fromZone && (
                    <div className="flex items-center gap-2 text-xs text-orange-600 bg-orange-50 rounded-lg px-3 py-2">
                      <AlertCircle className="w-4 h-4" />
                      <span>Zone not detected. Please select a recognized location.</span>
                    </div>
                  )}
                </div>

                {/* ✅ REPLACE zone Select with LocationInput for destination */}
                <div className="space-y-2">
                  <LocationInput
                    label="Destination"
                    value={formData?.destination}
                    onChange={(value, coords) => handleLocationChange('destination', value, coords)}
                    placeholder="Enter destination or select on map"
                    popularLocations={popularLocations}
                    error={errors?.destination || errors?.toZone}
                  />
                  {/* Show detected zone */}
                  {formData?.destination && formData?.toZone && (
                    <div className="flex items-center gap-2 text-xs text-gray-600 bg-blue-50 rounded-lg px-3 py-2">
                      <MapPin className="w-4 h-4 text-blue-600" />
                      <span>Zone <strong className="text-blue-700">{formData?.toZone}</strong> detected</span>
                    </div>
                  )}
                  {/* Show zone detection error */}
                  {formData?.destination && !formData?.toZone && (
                    <div className="flex items-center gap-2 text-xs text-orange-600 bg-orange-50 rounded-lg px-3 py-2">
                      <AlertCircle className="w-4 h-4" />
                      <span>Zone not detected. Please select a recognized location.</span>
                    </div>
                  )}
                </div>

                {/* Show pickup time - Responsive */}
                {bookingMode === 'immediate' && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 xxs:p-4">
                    <div className="flex items-center gap-2 xxs:gap-3">
                      <Clock className="w-4 h-4 xxs:w-5 xxs:h-5 text-green-600 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-xs xxs:text-sm font-semibold text-green-900">
                          {getTranslation('booking.form.estimatedPickup', currentLanguage) || 'Estimated Pickup'}
                        </p>
                        <p className="text-xs text-green-700">
                          {getTranslation('booking.form.asapPickup', currentLanguage) || 'As soon as possible (5-15 minutes)'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

              </div>
            </div>

            {/* ✅ UPDATE Early Price Preview messaging */}
            {formData?.pickupLocation && formData?.destination && (!formData?.fromZone || !formData?.toZone) && (
              <div className="bg-orange-50 border border-orange-200 rounded-2xl shadow-lg p-6 sm:p-8">
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-6 h-6 text-orange-600 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Zone Detection Required</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Please select recognized locations from the suggestions or map to calculate pricing.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* ✅ UPDATE price display messaging */}
            {formData?.selectedService && !calculatedPrice && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-2xl shadow-lg p-6 sm:p-8">
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Calculating price...</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {!formData?.fromZone || !formData?.toZone 
                        ? 'Select pickup area, destination, service and time to see the price.' :'Loading price information...'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Phone Number Input */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden" data-field="contactValue">
                <div className="p-6 sm:p-8 space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Phone className="w-4 h-4 inline mr-2 text-blue-600" />
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      value={formData?.contactValue || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, contactValue: e?.target?.value }))}
                      placeholder="+66812345678"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 placeholder-gray-400"
                    />
                    {errors?.contactValue && (
                      <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {errors?.contactValue}
                      </p>
                    )}
                  </div>

                  {/* Error Display */}
                  {orderError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 xxs:p-4">
                      <p className="text-xs xxs:text-sm text-red-600 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                        <span>{orderError}</span>
                      </p>
                    </div>
                  )}

                  {/* Book Now Button */}
                  <Button
                    onClick={handleSubmit}
                    disabled={!canProceed() || isSubmitting}
                    className={`w-full py-4 rounded-xl font-semibold text-white transition-all duration-200 flex items-center justify-center gap-2 ${
                      !canProceed() || isSubmitting
                        ? 'bg-gray-400 cursor-not-allowed' :'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 shadow-lg hover:shadow-xl hover:scale-[1.02]'
                    }`}
                  >
                    {isSubmitting ? (
                      <Loader text="Booking..." size="sm" className="text-white" />
                    ) : (
                      <>
                        <span>Book Now</span>
                      </>
                    )}
                  </Button>
                </div>
              </div>
          </div>

          {/* Price Summary Sidebar - Optimized for widescreen with sticky positioning */}
          <div className="space-y-6 sm:space-y-8">
            <div className="lg:sticky lg:top-24">
              {calculatedPrice && (
                <PriceDisplay
                  price={calculatedPrice}
                  service={availableServices?.find(s => s?.key === formData?.selectedService)}
                  pickupLocation={formData?.pickupLocation}
                  destination={formData?.destination}
                  route={priceDetails?.route || ''}
                  pickupDistrict={priceDetails?.pickupDistrict || ''}
                  destinationDistrict={priceDetails?.destinationDistrict || ''}
                  basePrice={priceDetails?.basePrice || 0}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SmartBookingForm;